/**
 * Keyboard handling utilities for mobile devices
 * Handles virtual keyboard appearance and viewport adjustments
 */

// Track keyboard state
let isKeyboardOpen = false;
let originalViewportHeight = window.innerHeight;
let keyboardListeners = [];

/**
 * Initialize keyboard handling
 */
export const initializeKeyboardHandler = () => {
  if (typeof window === 'undefined') return;

  // Detect keyboard open/close by viewport height changes
  const handleResize = () => {
    const currentHeight = window.innerHeight;
    const heightDifference = originalViewportHeight - currentHeight;
    
    // If height decreased by more than 150px, keyboard is likely open
    const keyboardOpen = heightDifference > 150;
    
    if (keyboardOpen !== isKeyboardOpen) {
      isKeyboardOpen = keyboardOpen;
      
      // Update CSS custom property for dynamic viewport height
      document.documentElement.style.setProperty(
        '--vh', 
        `${currentHeight * 0.01}px`
      );
      
      // Notify listeners
      keyboardListeners.forEach(callback => callback(isKeyboardOpen));
      
      // Add/remove class for styling
      document.body.classList.toggle('keyboard-open', isKeyboardOpen);
    }
  };

  // Listen for resize events
  window.addEventListener('resize', handleResize);
  
  // Listen for focus events on input elements
  const handleFocusIn = (event) => {
    if (event.target.matches('input, textarea, [contenteditable]')) {
      // Small delay to allow keyboard to appear
      setTimeout(() => {
        handleResize();
      }, 300);
    }
  };

  const handleFocusOut = (event) => {
    if (event.target.matches('input, textarea, [contenteditable]')) {
      // Small delay to allow keyboard to disappear
      setTimeout(() => {
        handleResize();
      }, 300);
    }
  };

  document.addEventListener('focusin', handleFocusIn);
  document.addEventListener('focusout', handleFocusOut);

  // Initial setup
  document.documentElement.style.setProperty(
    '--vh', 
    `${window.innerHeight * 0.01}px`
  );

  // Cleanup function
  return () => {
    window.removeEventListener('resize', handleResize);
    document.removeEventListener('focusin', handleFocusIn);
    document.removeEventListener('focusout', handleFocusOut);
    document.body.classList.remove('keyboard-open');
  };
};

/**
 * Add keyboard state listener
 */
export const addKeyboardListener = (callback) => {
  keyboardListeners.push(callback);
  
  // Return cleanup function
  return () => {
    const index = keyboardListeners.indexOf(callback);
    if (index > -1) {
      keyboardListeners.splice(index, 1);
    }
  };
};

/**
 * Check if keyboard is currently open
 */
export const isKeyboardCurrentlyOpen = () => isKeyboardOpen;

/**
 * Scroll element into view when keyboard opens
 */
export const scrollIntoViewOnKeyboard = (element) => {
  if (!element) return;

  const cleanup = addKeyboardListener((isOpen) => {
    if (isOpen) {
      // Small delay to allow keyboard animation
      setTimeout(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
    }
  });

  return cleanup;
};

/**
 * Prevent body scroll when keyboard is open (iOS)
 */
export const preventBodyScrollOnKeyboard = () => {
  const cleanup = addKeyboardListener((isOpen) => {
    if (isOpen) {
      // Prevent body scroll on iOS
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Restore body scroll
      document.body.style.position = '';
      document.body.style.width = '';
    }
  });

  return cleanup;
};


